import { defineNuxtPlugin } from '#app'

/**
 * Patches the native `StorageEvent` constructor to tolerate non-native Storage proxies.
 *
 * Root cause:
 * `@vueuse/core`'s `useStorage` dispatches a custom storage event via
 * `new StorageEvent('storage', { ...payload, storageArea: storage })`.
 * Some production environments (privacy browser extensions / storage proxies) expose a
 * `window.localStorage` that passes `instanceof Storage` but is NOT a true native `Storage`.
 * The native `StorageEvent` constructor validates `storageArea` against the real `Storage`
 * interface and throws:
 *   "Failed to construct 'StorageEvent': Failed to read the 'storageArea' property from
 *    'StorageEventInit': Failed to convert value to 'Storage'."
 *
 * Fix:
 * Wrap the constructor so that, when construction throws because of `storageArea`, we retry
 * once WITHOUT that single offending property — preserving every other behavior. Any other
 * failure is re-thrown untouched so genuine errors stay loud.
 */
export default defineNuxtPlugin(() => {
  if (typeof window === 'undefined')
    return

  const patchTarget = window as unknown as { __storageEventPatched?: boolean }
  if (patchTarget.__storageEventPatched)
    return

  const NativeStorageEvent = window.StorageEvent
  if (typeof NativeStorageEvent !== 'function')
    return

  function PatchedStorageEvent(type: string, init?: StorageEventInit): StorageEvent {
    try {
      return new NativeStorageEvent(type, init)
    }
    catch (error) {
      if (!init || !('storageArea' in init))
        throw error

      const { storageArea: _storageArea, ...initWithoutStorageArea } = init
      return new NativeStorageEvent(type, initWithoutStorageArea)
    }
  }

  // Preserve prototype chain so `event instanceof StorageEvent` keeps working.
  PatchedStorageEvent.prototype = NativeStorageEvent.prototype

  window.StorageEvent = PatchedStorageEvent as unknown as typeof StorageEvent
  patchTarget.__storageEventPatched = true
})
